import { Pipe, PipeTransform } from '@angular/core';
import { iif, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { compareDesc, compareAsc } from 'date-fns';
import groupBy from 'lodash-es/groupBy';
import omit from 'lodash-es/omit';

import {
  AssistanceByReservationItem, UserHistoryItemVmOrder, ReservationType, UserHistoryItem, ReservationItems
} from '@user-history/entities/user-history-item';
import { UserHistoryFacade } from '@user-history/user-history.facade';
import { UserHistoryOptions } from '@user-history/entities/user-history-options';
import {
  getCrossSellingAssistanceByProductItem, getCrossSellingByProductItem
} from '@shared/cross-selling/mappers/cross-selling-by-item.mapper';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { getReservationItem } from '@app/pipes/mappers/reservation-items.mapper';
import { isCancelledReservationByStatus } from '@ux/mappers/reservation-status.mapper';
import { ReservationStatus } from '@user-history/entities/reservation-status';
import { TzDatesHelper } from '@ux/helpers/tz-dates.helper';

@Pipe({
  name: 'historyListByTab',
  standalone: false
})
export class HistoryListByTabPipe implements PipeTransform {

  constructor(
    private userHistoryFacade: UserHistoryFacade
  ) { }

  transform(
    opposite: UserHistoryOptions = null
  ): Observable<UserHistoryItem[]> {
    return iif(
      () => !!opposite,
      of(opposite),
      this.userHistoryFacade.UiTabSelected$
    ).pipe(
      switchMap(tab => this.userHistoryFacade.userHistoryListConfirmed$.pipe
      (
        map(history => {
          let formatHistory = [];
          switch (tab) {
            case UserHistoryOptions.UPCOMING:
              formatHistory = sortHistory(getUpcomingList(history), true);
              break;
            case UserHistoryOptions.ACCOMPLISHED:
              formatHistory = sortHistory(getAccomplishedList(history), false);
              break;
          }
          return formatHistory.map(item => {
            const assistance = getReservationAsisstance(item, history);
            return {
              ...item,
              AssistanceItem: assistance,
              CrossSellingByProducts: [
                getCrossSellingAssistanceByProductItem(item, assistance, tab),
                getCrossSellingByProductItem(item, formatHistory, tab),
              ].filter(cross => !isEmpty(cross))
            };
          })
        }),
      )),
      map(results => results)
    );
  }
}

function getUpcomingList(history: UserHistoryItem[]): UserHistoryItem[] {
  return history
    .map(item => ({
      ...item,
      ReservationItems: getReservationItem(item).filter(reservation => !isEmpty(reservation))
    }))
    .filter(
      item => !isEmpty(item?.ReservationItems?.[0]) &&
        item.ReservationStatus !== ReservationStatus.Expired
    ).filter(item => {
      return new TzDatesHelper().isDateAfterToday(item?.ReservationItems?.[0]?.EndDate);
    });
}

function getAccomplishedList(history: UserHistoryItem[]): UserHistoryItem[] {
  return history
    .map(item => ({
      ...item,
      ReservationItems: getReservationItem(item).filter(reservation => !isEmpty(reservation))
    }))
    .filter(
      item => !isEmpty(item?.ReservationItems?.[0]) && item.ReservationStatus !== ReservationStatus.Expired
    ).filter(item => {
      return new TzDatesHelper().isDateBeforeToday(item?.ReservationItems?.[0]?.EndDate);
    });
}

function getReservationAsisstance(item: UserHistoryItem, rawHistory: UserHistoryItem[]): AssistanceByReservationItem {
  const rawItem: UserHistoryItem = rawHistory?.find(raw => raw.Id === item.Id);
  if (rawItem) {
    const assistance = rawItem?.ReservationItems?.find(_raw => _raw.ReservationItemType === ReservationType.Assistance);
    return assistance ? {
      ...assistance,
      VisibleInTheListResume: isVisibleAssistanceInResumeList(item, assistance),
    } as AssistanceByReservationItem : null;
  } else return null;
}

function isVisibleAssistanceInResumeList(item: UserHistoryItem, assistanceNotFormat: ReservationItems): boolean {
  return !!isCancelledReservationByStatus(item.ReservationStatus as ReservationStatus) || (
    !isCancelledReservationByStatus(item.ReservationStatus as ReservationStatus) &&
    !isCancelledReservationByStatus(assistanceNotFormat.ReservationStatus as ReservationStatus)
  );
}

function sortHistory(history: UserHistoryItem[], orderDesc: boolean): UserHistoryItem[] {
  const list = history.filter(item => !isEmpty(item?.ReservationItems?.[0]));
  let order: UserHistoryItem[] = [];
  order = sortByStartDate(list, orderDesc);
  const formatCollection = getReservationItemsVmByOrder(order);
  const groupByStartDateFormat = groupBy(formatCollection, 'ReservationStartDateFormat');
  order = getFinalSortCollection(groupByStartDateFormat, orderDesc);
  return order;
}

function sortByStartDate(history: UserHistoryItem[], orderDesc: boolean): UserHistoryItem[] {
  return history.sort((current, next) => {
    const currentItem = current.ReservationItems[0];
    const nextItem = next.ReservationItems[0];
    if (orderDesc) {
      return compareDesc(new Date(nextItem.StartDate), new Date(currentItem.StartDate));
    } else return compareAsc(new Date(nextItem.StartDate), new Date(currentItem.StartDate));
  });
}

function getFinalSortCollection(
  group: { [key: string]: UserHistoryItemVmOrder[] }, orderDesc: boolean
): UserHistoryItem[] {
  let finalCollection: UserHistoryItem[] = [];
  for (const startDate in group) {
    if (Object.prototype.hasOwnProperty.call(group, startDate)) {
      const collection = group[startDate];
      const sort = sortByEndDate(collection, orderDesc);
      finalCollection = finalCollection.concat(sort);
    }
  }
  return finalCollection;
}

function sortByEndDate(history: UserHistoryItemVmOrder[], orderDesc: boolean): UserHistoryItem[] {
  return history.sort((current, next) => {
    const currentItem = current.ReservationItems[0];
    const nextItem = next.ReservationItems[0];
    if (orderDesc) {
      return compareDesc(new Date(nextItem.EndDate), new Date(currentItem.EndDate));
    } else return compareAsc(new Date(nextItem.EndDate), new Date(currentItem.EndDate));
  }).map(item => omit(item, ['ReservationStartDateFormat', 'ReservationEndDateFormat']));
}

function getReservationItemsVmByOrder(history: UserHistoryItem[]): UserHistoryItemVmOrder[] {
  return history.map(item => {
    const reservation = item.ReservationItems[0];
    return {
      ...item,
      ReservationStartDateFormat: formatStrinWithTimeZoneDateByRegex(reservation.StartDate),
      ReservationEndDateFormat: formatStrinWithTimeZoneDateByRegex(reservation.EndDate),
    };
  });
}
